const SETTING_DATE_REGEX =
  /(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2})(?::(\d{2}))?\s*(AM|PM)/i;

/**
 * Parse a wall-clock date string in formats like:
 *   "5/27/2026 8:00 AM"
 *   "5/27/2026 8AM"
 *   "5/27/2026 8 AM"
 * into discrete numeric components. The values are meant to be interpreted as
 * Eastern Time. Returns null if the string does not look like a valid date.
 */
export function parseEasternWallClock(value) {
  if (!value || typeof value !== "string") return null;
  const match = value.match(SETTING_DATE_REGEX);
  if (!match) return null;
  let [, monthStr, dayStr, yearStr, hourStr, minuteStr, ampm] = match;
  let hour = parseInt(hourStr, 10);
  const minute = minuteStr ? parseInt(minuteStr, 10) : 0;
  if (ampm.toUpperCase() === "PM" && hour !== 12) hour += 12;
  if (ampm.toUpperCase() === "AM" && hour === 12) hour = 0;
  return {
    year: parseInt(yearStr, 10),
    month: parseInt(monthStr, 10),
    day: parseInt(dayStr, 10),
    hour,
    minute,
  };
}

/**
 * Reads the current wall-clock time in US Eastern Time (handles EST/EDT
 * automatically via the IANA zone).
 */
export function getCurrentEasternComponents(now = new Date()) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(now);
    const get = (t) => parts.find((p) => p.type === t)?.value;
    let hour = parseInt(get("hour"), 10);
    // Some browsers (older Safari) emit "24" for midnight; normalize.
    if (Number.isNaN(hour)) hour = 0;
    if (hour === 24) hour = 0;
    return {
      year: parseInt(get("year"), 10),
      month: parseInt(get("month"), 10),
      day: parseInt(get("day"), 10),
      hour,
      minute: parseInt(get("minute"), 10) || 0,
    };
  } catch (e) {
    // Fallback to local time if Intl with timeZone isn't supported.
    return {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
      hour: now.getHours(),
      minute: now.getMinutes(),
    };
  }
}

/**
 * Turn discrete wall-clock components into a single sortable number so we can
 * compare them lexicographically without going through Date/UTC math.
 */
export function componentsToSortable(c) {
  if (!c) return null;
  return (
    c.year * 100000000 +
    c.month * 1000000 +
    c.day * 10000 +
    c.hour * 100 +
    c.minute
  );
}

/**
 * Returns true when the current Eastern wall-clock time is between
 * `startStr` and `endStr` (inclusive of both endpoints). If either bound is
 * missing or invalid the function returns false so the UI fails closed.
 */
export function isWithinEasternWindow(startStr, endStr, now = new Date()) {
  const start = componentsToSortable(parseEasternWallClock(startStr));
  const end = componentsToSortable(parseEasternWallClock(endStr));
  if (start == null || end == null) return false;
  const current = componentsToSortable(getCurrentEasternComponents(now));
  return current >= start && current <= end;
}

/**
 * Normalize a hex color string so we always end up with a valid `#rrggbb`
 * (or `#rgb`) value usable in CSS. Accepts strings with or without the leading
 * `#`. Returns null when the value is empty/invalid so the caller can fall
 * back to a default.
 */
export function normalizeHexColor(value) {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const withoutHash = trimmed.replace(/^#/, "");
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(withoutHash)) return null;
  return `#${withoutHash.toLowerCase()}`;
}
